export type Volume = {
  number: string
  title: string
  subtitle: string
  intro: string
  problem: string
  listLabel: string
  bullets: string[]
  result: string
}

export const volumes: Volume[] = [
  {
    number: 'Volume 1',
    title: 'Tap Foundations & Core Technique',
    subtitle:
      'Build the technical foundation students need before complexity is added.',
    intro:
      'Teaching strong Tap Dance begins with more than showing steps. Students need weight placement, clean articulation, controlled transfers, clear sound production, coordination, timing, and technical habits that can support everything that comes later.',
    problem:
      'This volume gives you a structured technical base for developing students correctly from the beginning instead of constantly correcting foundational problems later.',
    listLabel: 'Inside you will work with:',
    bullets: [
      'Core Tap Dance movement principles',
      'Weight placement and transfer',
      'Foot articulation and sound clarity',
      'Foundational vocabulary',
      'Shuffles, flaps, ball changes, time-step foundations, and essential actions',
      'Coordination and control',
      'Technical preparation',
      'Common errors and corrections',
      'Progression criteria',
      'Practical instructor applications',
    ],
    result:
      'Students develop cleaner technique, better control, and stronger foundations for everything that follows.',
  },
  {
    number: 'Volume 2',
    title: 'Beginner Tap Curriculum & Class System',
    subtitle:
      'Turn beginner instruction into a clear, progressive teaching pathway.',
    intro:
      'Beginner students should not receive random steps every week. They need a logical sequence that allows technique, coordination, rhythm, confidence, and vocabulary to develop together.',
    problem:
      'You no longer have to constantly ask yourself what beginners should learn next. This volume provides a clear structure for organizing beginner Tap Dance instruction into progressive classes.',
    listLabel: 'Inside you will find:',
    bullets: [
      'Beginner-level teaching progressions',
      'Class sequencing',
      'Foundational rhythm development',
      'Vocabulary expansion',
      'Simple combinations',
      'Coordination training',
      'Progressive technical challenges',
      'Teacher cues and corrections',
      'Student readiness criteria',
      'Practical class structures',
    ],
    result:
      'Your beginner classes become more organized, progressive, and easier to teach consistently.',
  },
  {
    number: 'Volume 3',
    title: 'Intermediate Tap Technique, Rhythm & Musicality',
    subtitle:
      'Move students beyond basic execution and into real rhythmic control.',
    intro:
      'Intermediate Tap Dance is where students need to do more than remember steps. They need clearer sounds, stronger timing, faster transitions, more complex coordination, and a deeper relationship with rhythm and music.',
    problem:
      'This volume provides the bridge between beginner vocabulary and more demanding technical and musical work.',
    listLabel: 'Inside you will work with:',
    bullets: [
      'Intermediate technique',
      'Rhythm combinations',
      'Timing development',
      'Subdivisions',
      'Syncopation',
      'Musical phrasing',
      'Coordination challenges',
      'Combination construction',
      'Progressive complexity',
      'Corrections and instructional cues',
    ],
    result:
      'Students stop simply performing steps and begin dancing with greater rhythmic understanding, precision, and musical awareness.',
  },
  {
    number: 'Volume 4',
    title: 'Advanced Tap Technique, Speed, Power & Performance',
    subtitle:
      'Develop advanced students without sacrificing clarity or control.',
    intro:
      'Advanced Tap requires more speed and complexity—but speed without technical control creates noise instead of precision.',
    problem:
      'This volume helps you challenge advanced students through structured progressions rather than simply making combinations faster or longer.',
    listLabel: 'Inside you will work with:',
    bullets: [
      'Advanced technical development',
      'Speed-building systems',
      'Clarity under tempo',
      'Power and dynamic range',
      'Advanced combinations',
      'Complex transitions',
      'Performance quality',
      'Technical endurance',
      'Precision under pressure',
      'Advanced troubleshooting and correction',
    ],
    result:
      'Your advanced classes become more demanding, purposeful, technically sound, and performance-ready.',
  },
  {
    number: 'Volume 5',
    title: 'Rhythm, Musicality, Improvisation & Creative Tap Mastery',
    subtitle:
      'Teach students to hear, understand, create, and respond—not only memorize.',
    intro:
      'Tap Dance is both movement and music. Technical ability alone does not create a complete Tap dancer.',
    problem:
      'This volume gives you a structured way to teach the musical and creative skills that are often difficult to organize into regular classes.',
    listLabel: 'Inside you will explore:',
    bullets: [
      'Pulse and rhythmic awareness',
      'Subdivisions, accents, and syncopation',
      'Phrasing and call-and-response',
      'Musical interpretation',
      'Improvisation frameworks',
      'Creative exercises',
      'Combination development',
      'Listening skills',
      'Performance expression',
    ],
    result:
      'Students become more musical, adaptable, creative, and confident instead of depending entirely on memorized choreography.',
  },
  {
    number: 'Volume 6',
    title: 'Complete Teaching System, 150 Lessons, Assessment & Class Planning',
    subtitle: 'The volume that turns everything into classes you can actually teach.',
    intro:
      'This is where the complete system comes together. Instead of understanding what should be taught but still having to build the actual lesson, you have a professional library of 150 ready-to-teach Tap Dance classes organized across levels and teaching objectives.',
    problem:
      'You choose the appropriate class, understand the objective, and teach with a clear structure instead of starting from a blank page.',
    listLabel: 'Includes:',
    bullets: [
      '50 Beginner lessons',
      '50 Intermediate lessons',
      '50 Advanced and Performance lessons',
      'Complete class structures',
      'Technical objectives',
      'Rhythm and musicality applications',
      'Progressive lesson sequencing',
      'Assessment systems',
      'Class-planning tools',
      'Student progression criteria',
      'Instructor decision frameworks',
    ],
    result:
      'You can choose the appropriate class, understand the objective, and teach with a clear structure instead of starting from a blank page.',
  },
]
